import React from 'react';

import { createDocumentRoute, parseMarkdownLocationLink } from '@aline/common';
import { params } from '@aline/routes';
import { useScrollToLocationForReference } from 'src/editor';
import { openLink, useDispatchNotification, useMaybeDocumentParams } from 'src/helpers';
import { useGetDocumentHeader } from 'src/hooks/document';
import { useMaybeEditor } from 'src/layout-engine';

import { Citation } from '../../../../shared/components/Citation';
import { CitationCard } from '../../../../shared/components/CitationCard';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { createAIChatItem } from '../../helpers/aiChatItem';
import { parseLocation } from '../../helpers/parseLocation';

interface AIChatCitationProps {
  citationIndex: number;
  href: string;
  messageId: string;
  referencedText: string;
  threadId: string;
}

/**
 * Container component specifically for AI chat citations.
 * This component handles the business logic for AI chat citations,
 * then renders the presentational Citation component.
 */
export const AIChatCitation = React.memo(function AIChatCitation({
  citationIndex,
  href,
  messageId,
  referencedText,
  threadId,
  ...rest
}: AIChatCitationProps & MarkdownComponentProps<HTMLAnchorElement>): JSX.Element | null {
  const editor = useMaybeEditor();
  const { documentId } = useMaybeDocumentParams();
  const parts = parseMarkdownLocationLink(href);
  const citesThisDocument = Boolean(parts && documentId && parts.documentId === documentId);
  const { document: citedDocument } = useGetDocumentHeader(parts?.documentId ?? null);
  const { scrollToLocationForReference } = useScrollToLocationForReference();
  const dispatchLocationNotFoundNotification = useDispatchNotification({
    message:
      'Document location not found. This can occur if the document has been updated since the citation was generated.',
    variant: 'warning',
  });
  if (!parts) return null;
  const location = parts;

  const elementId = createAIChatItem({
    index: citationIndex,
    messageId,
    threadId,
    type: 'citation',
  });
  function openCitation() {
    if (!citesThisDocument) {
      openLink(
        createCitationDocumentRoute({
          citationIndex,
          href,
          location,
          messageId,
          referencedText,
          threadId,
        }),
        {
          applyRouteToExistingTab: true,
        }
      );
      return;
    }

    const aiReference = parseLocation(href, referencedText, editor);
    if (!aiReference) {
      dispatchLocationNotFoundNotification();
      return;
    }

    scrollToLocationForReference({ aiReference });
  }

  const handleClickCitation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openCitation();
  };

  /**
   * Important: {...rest} must be first so that id is correct and not overridden.
   * data-citation-href and data-referenced-text are read by useScrollToChatItemEffect
   * to scroll the document canvas when navigating from /chat to the editor.
   */
  return (
    <Citation
      {...rest}
      card={
        <CitationCard
          caption={citesThisDocument ? 'This document' : null}
          jumpInPlace={citesThisDocument}
          title={citedDocument?.name ?? ''}
          onViewInDocument={openCitation}
        />
      }
      data-citation-href={href}
      data-referenced-text={referencedText}
      id={elementId}
      tooltip={
        citedDocument?.name || (citesThisDocument ? 'View citation' : 'Open referenced document')
      }
      onClickCitation={handleClickCitation}
    />
  );
});

function createCitationDocumentRoute({
  citationIndex,
  href,
  location,
  messageId,
  referencedText,
  threadId,
}: {
  citationIndex: number;
  href: string;
  location: { documentId: string };
  messageId: string;
  referencedText: string;
  threadId: string;
}): string {
  const route = createDocumentRoute(location.documentId, {
    aiChatItem: createAIChatItem({
      index: citationIndex,
      messageId,
      threadId,
      type: 'citation',
    }),
  });
  const searchParams = new URLSearchParams();
  searchParams.set(params.citation, href);
  if (referencedText) searchParams.set(params.citationText, referencedText);

  const separator = route.includes('?') ? '&' : '?';
  return `${route}${separator}${searchParams.toString()}`;
}
